import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Button } from '../common';
import { Heading, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { homeSectionHeading, homeUpcomingSessionCardShell, homeElevatedInset } from '../home/homeDashboardStyles';
import { interactiveBleedInsetX } from '../../constants/interactiveSafeInsets';
import { fetchPlayerRecentSessions } from '../../services/playerService';

const sessionsStripClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x`;
const sessionsCardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start';

const ICON = '🏸';
const ICON_BG = 'bg-violet-100 dark:bg-violet-900/30';
const ICON_TEXT = 'text-violet-700 dark:text-violet-400';

/** @typedef {Object} PlayerRecentSessionRaw */

/**
 * @typedef {Object} RecentSessionCarouselItem
 * @property {string} id
 * @property {string} clubId
 * @property {string} name
 * @property {string} [clubName]
 * @property {string|null} [clubLogoImageUrl]
 * @property {Date|string|null} [date]
 * @property {number} [gamesPlayed]
 * @property {number} [wins]
 * @property {number} [losses]
 * @property {number|null} [winRatePct]
 * @property {number} [mixedEloDelta]
 * @property {number} [levelsEloDelta]
 * @property {number} [totalEloDelta]
 */

/** @param {PlayerRecentSessionRaw} session */
export function normalizePlayerRecentSession(session) {
  const gamesPlayed = session.gamesPlayed ?? 0;
  const wins = session.wins ?? 0;
  return {
    id: session.sessionId,
    clubId: session.clubId,
    name: session.sessionName || 'Session',
    clubName: session.clubName,
    clubLogoImageUrl: session.clubLogoImageUrl ?? null,
    date: session.latestGameDate || session.sessionDate || null,
    gamesPlayed,
    wins,
    losses: session.losses ?? 0,
    winRatePct: gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : null,
    mixedEloDelta: session.mixedEloDelta ?? 0,
    levelsEloDelta: session.levelsEloDelta ?? 0,
    totalEloDelta: session.totalEloChange,
  };
}

/** @param {{ sessionId: string, clubId: string, sessionName: string, clubName?: string, latestDate?: Date|null, gamesPlayed: number, wins: number, losses: number, winRate?: number, totalEloChange?: number }} session */
export function normalizeGameDerivedSession(session) {
  return {
    id: session.sessionId,
    clubId: session.clubId,
    name: session.sessionName || 'Session',
    clubName: session.clubName,
    clubLogoImageUrl: null,
    date: session.latestDate || null,
    gamesPlayed: session.gamesPlayed,
    wins: session.wins,
    losses: session.losses,
    winRatePct: session.winRate ?? (session.gamesPlayed > 0
      ? Math.round((session.wins / session.gamesPlayed) * 100)
      : null),
    mixedEloDelta: 0,
    levelsEloDelta: 0,
    totalEloDelta: session.totalEloChange ?? 0,
  };
}

function formatSessionDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : parseISO(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'EEE, d MMM');
}

function getWinRateColor(winRatePct) {
  if (winRatePct == null) return 'text-text-muted';
  if (winRatePct > 50) return 'text-success';
  if (winRatePct < 50) return 'text-danger';
  return 'text-text-secondary';
}

function SessionCarouselCard({ session, onSelect }) {
  const dateStr = formatSessionDate(session.date);
  const winRatePct = session.winRatePct;
  const gamesPlayed = session.gamesPlayed ?? 0;

  return (
    <Card
      variant="default"
      className={`flex flex-col gap-3 ${homeElevatedInset} ${sessionsCardClass}`}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-lg shrink-0 ${ICON_BG} ${ICON_TEXT}`}
          aria-hidden
        >
          {ICON}
        </span>
        <Typography variant="caption" className="text-text-muted uppercase tracking-wide text-[0.65rem] font-semibold">
          Completed
        </Typography>
      </div>

      <div className="flex flex-col gap-1 min-w-0">
        <Typography className="font-semibold leading-snug text-text-primary text-sm line-clamp-2">
          {session.name}
        </Typography>
        <Typography variant="caption" className="text-text-secondary leading-snug">
          {dateStr || 'TBD'}
          {gamesPlayed > 0 ? ` · ${gamesPlayed} game${gamesPlayed !== 1 ? 's' : ''}` : ''}
        </Typography>
        {winRatePct != null ? (
          <Typography
            variant="caption"
            className={`font-semibold tabular-nums leading-snug ${getWinRateColor(winRatePct)}`}
          >
            {winRatePct}% win rate
          </Typography>
        ) : null}
      </div>

      <div className="mt-auto">
        <Button
          variant="primary"
          size="small"
          className="w-full text-xs font-semibold"
          onClick={() => onSelect(session)}
        >
          View my performance
        </Button>
      </div>
    </Card>
  );
}

/**
 * Horizontal snap carousel of recent session cards with optional "View all" link.
 */
export function PlayerRecentSessionsCarousel({
  sessions: sessionsProp,
  autoFetch = false,
  fetchLimit = 5,
  loading: loadingProp = false,
  currentUser,
  title = 'My Recent Sessions',
  viewAllTo = '/profile/sessions',
  viewAllLabel = 'View all',
  maxItems = 5,
  emptyMessage = 'No recent sessions yet — play a session to see your activity here.',
  className = '',
  headingClassName = homeSectionHeading,
}) {
  const navigate = useNavigate();
  const [fetchedSessions, setFetchedSessions] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(autoFetch);

  useEffect(() => {
    if (!autoFetch || !currentUser?.id) {
      setFetchLoading(false);
      return;
    }

    let cancelled = false;
    setFetchLoading(true);

    fetchPlayerRecentSessions(currentUser.id, fetchLimit)
      .then((rows) => {
        if (cancelled) return;
        setFetchedSessions((rows || []).map(normalizePlayerRecentSession));
      })
      .catch((err) => {
        console.error('Error loading recent sessions carousel:', err);
        if (!cancelled) setFetchedSessions([]);
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [autoFetch, currentUser?.id, fetchLimit]);

  const sessions = sessionsProp ?? fetchedSessions;
  const loading = loadingProp || fetchLoading;
  const visibleSessions = sessions.slice(0, maxItems);

  const openSession = (session) => {
    if (!session?.id || !session?.clubId || !currentUser?.id) return;
    navigate(`/player/${currentUser.id}/session/${session.id}`, {
      state: { clubId: session.clubId },
    });
  };

  if (loading) {
    return (
      <div className={className}>
        <Heading level={2} className={`${headingClassName} mb-5`}>{title}</Heading>
        <div className="text-center py-8 rounded-2xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">Loading sessions...</Typography>
        </div>
      </div>
    );
  }

  if (!visibleSessions.length) {
    return (
      <div className={className}>
        <Heading level={2} className={`${headingClassName} mb-5`}>{title}</Heading>
        <Card variant="default" className={`${homeUpcomingSessionCardShell} text-center py-8 px-6`}>
          <Typography variant="body-small" className="text-text-secondary">
            {emptyMessage}
          </Typography>
        </Card>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-5 gap-3">
        <Heading level={2} className={headingClassName}>{title}</Heading>
        {viewAllTo ? (
          <Button
            type="button"
            onClick={() => navigate(viewAllTo)}
            variant="text"
            className="text-sm shrink-0 font-semibold text-primary"
          >
            {viewAllLabel}
          </Button>
        ) : null}
      </div>

      <div role="region" aria-label={title} className={sessionsStripClass}>
        {visibleSessions.map((session) => (
          <SessionCarouselCard
            key={`${session.clubId}-${session.id}`}
            session={session}
            onSelect={openSession}
          />
        ))}
      </div>

      {sessions.length > 1 ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe for more
        </Typography>
      ) : null}
    </div>
  );
}
